/**
 * Form Builder — per-section form configuration (action, method, hidden fields, endpoint presets).
 * Renders into the Properties tab when the selected section contains a <form>.
 */

import { store } from '../core/state.js';
import { showConfirmModal, showUndoToast } from './shell.js';

const FORM_DEFAULTS = {
  action: '',
  method: 'POST',
  successMessage: 'Thank you! Your submission has been received.',
  hiddenFields: [],
  endpoint: 'custom',
  handleSubmit: true
};

const ENDPOINT_PRESETS = [
  { value: 'custom', label: 'Custom' },
  { value: 'netlify', label: 'Netlify' },
  { value: 'formspree', label: 'Formspree' },
  { value: 'worker', label: 'Cloudflare Worker' }
];

/**
 * Detect if section HTML contains a <form> element (DOM-based, not string matching).
 */
export function sectionHasForm(html) {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  return !!doc.querySelector('form');
}

export class FormBuilder {
  constructor(container, instanceId) {
    this._container = container;
    this._instanceId = instanceId;
    this._destroyed = false;
    this._render();
  }

  _getFormConfig() {
    const section = this._getSection();
    if (!section?.overrides?.formConfig) {
      return { ...FORM_DEFAULTS, hiddenFields: [] };
    }
    const cfg = section.overrides.formConfig;
    return {
      ...FORM_DEFAULTS,
      ...cfg,
      hiddenFields: (cfg.hiddenFields || []).map((f) => ({ ...f }))
    };
  }

  _getSection() {
    const state = store.getState();
    for (const page of state.pages) {
      const s = page.sections.find((s) => s.instanceId === this._instanceId);
      if (s) return s;
    }
    return null;
  }

  _getSectionPath() {
    const state = store.getState();
    for (let pi = 0; pi < state.pages.length; pi++) {
      const si = state.pages[pi].sections.findIndex((s) => s.instanceId === this._instanceId);
      if (si >= 0) return `pages.${pi}.sections.${si}`;
    }
    return null;
  }

  _updateConfig(field, value) {
    const path = this._getSectionPath();
    if (!path) return;
    const config = this._getFormConfig();
    config[field] = value;
    store.update(`${path}.overrides.formConfig`, config, `Update form ${field}`);
  }

  _render() {
    const config = this._getFormConfig();
    const presetOptions = ENDPOINT_PRESETS.map(
      (p) =>
        `<option value="${p.value}" ${config.endpoint === p.value ? 'selected' : ''}>${p.label}</option>`
    ).join('');

    const hiddenRows = (config.hiddenFields || [])
      .map(
        (f, i) => `
      <div class="pb-form-hidden-row" data-hidden-idx="${i}">
        <input type="text" class="pb-input pb-form-hidden-name" value="${esc(f.name)}" placeholder="name" aria-label="Hidden field name" spellcheck="false" />
        <input type="text" class="pb-input pb-form-hidden-value" value="${esc(f.value)}" placeholder="value" aria-label="Hidden field value" spellcheck="false" />
        <button class="pb-btn pb-btn-sm pb-btn-outline pb-form-remove-hidden" data-idx="${i}" aria-label="Remove" data-tooltip="Remove">&times;</button>
      </div>
    `
      )
      .join('');

    this._container.innerHTML = `
      <div class="pb-props-section">
        <button class="pb-props-section-title pb-collapsible" data-pb-collapse="form" aria-expanded="true" aria-controls="pb-collapse-form">
          Form Settings
          <svg class="pb-collapse-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>
        <div class="pb-collapsible-body" data-pb-collapse-body="form" id="pb-collapse-form">
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-form-endpoint">Endpoint</label>
            <select class="pb-input" id="pb-form-endpoint">${presetOptions}</select>
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label" for="pb-form-action">Action URL</label>
            <input type="text" class="pb-input" id="pb-form-action" value="${esc(config.action)}" placeholder="${getActionPlaceholder(config.endpoint)}" spellcheck="false" />
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label">Method</label>
            <select class="pb-input" id="pb-form-method">
              <option value="POST" ${config.method === 'POST' ? 'selected' : ''}>POST</option>
              <option value="GET" ${config.method === 'GET' ? 'selected' : ''}>GET</option>
            </select>
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label">
              <input type="checkbox" id="pb-form-handle-submit" ${config.handleSubmit ? 'checked' : ''} />
              Handle submission with JS
            </label>
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label">Success Message</label>
            <textarea class="pb-input" id="pb-form-success" rows="2" spellcheck="false">${esc(config.successMessage)}</textarea>
          </div>
          <div class="pb-override-row">
            <label class="pb-props-label">Hidden Fields</label>
            <div id="pb-form-hidden-list">${hiddenRows}</div>
            <button class="pb-btn pb-btn-sm pb-btn-outline" id="pb-form-add-hidden" style="margin-top:4px">+ Add Field</button>
          </div>
        </div>
      </div>
    `;

    this._bind();
  }

  _bind() {
    const el = this._container;

    // Collapsible
    const collapseTitle = el.querySelector('[data-pb-collapse="form"]');
    if (collapseTitle) {
      collapseTitle.addEventListener('click', () => {
        const body = el.querySelector('[data-pb-collapse-body="form"]');
        if (body) {
          body.classList.toggle('pb-collapsed');
          collapseTitle.classList.toggle('pb-collapsed');
          const expanded = !body.classList.contains('pb-collapsed');
          collapseTitle.setAttribute('aria-expanded', String(expanded));
        }
      });
    }

    // Endpoint preset — batch all changes into one undo entry
    el.querySelector('#pb-form-endpoint')?.addEventListener('change', (e) => {
      const ep = e.target.value;
      const path = this._getSectionPath();
      if (!path) return;
      const config = this._getFormConfig();
      config.endpoint = ep;
      config.handleSubmit = ep === 'custom' || ep === 'worker';
      config.action = ''; // Clear action on preset change
      store.update(`${path}.overrides.formConfig`, config, 'Change form endpoint');
      this._render();
    });

    // Action URL
    const actionInput = el.querySelector('#pb-form-action');
    if (actionInput) {
      actionInput.addEventListener('focus', () => store.beginTransaction('Edit form action'));
      actionInput.addEventListener('blur', () => {
        store.commitTransaction();
        this._updateConfig('action', actionInput.value.trim());
      });
      actionInput.addEventListener('input', () => {
        this._updateConfig('action', actionInput.value.trim());
      });
    }

    // Method
    el.querySelector('#pb-form-method')?.addEventListener('change', (e) => {
      this._updateConfig('method', e.target.value);
    });

    // Handle submit checkbox
    el.querySelector('#pb-form-handle-submit')?.addEventListener('change', (e) => {
      this._updateConfig('handleSubmit', e.target.checked);
    });

    // Success message
    const successInput = el.querySelector('#pb-form-success');
    if (successInput) {
      successInput.addEventListener('focus', () => store.beginTransaction('Edit success message'));
      successInput.addEventListener('blur', () => {
        store.commitTransaction();
        this._updateConfig('successMessage', successInput.value);
      });
      successInput.addEventListener('input', () => {
        this._updateConfig('successMessage', successInput.value);
      });
    }

    // Hidden fields — remove
    el.querySelectorAll('.pb-form-remove-hidden').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const idx = parseInt(btn.dataset.idx, 10);
        const config = this._getFormConfig();
        const field = config.hiddenFields[idx];
        const label = field?.name ? `"${field.name}"` : 'this hidden field';
        const confirmed = await showConfirmModal({
          title: 'Delete Hidden Field',
          message: `Delete ${label}?`,
          details: 'This field will be removed from this form configuration.',
          confirmLabel: 'Delete Field',
          cancelLabel: 'Cancel',
          danger: true
        });
        if (!confirmed) return;
        config.hiddenFields.splice(idx, 1);
        this._updateConfig('hiddenFields', config.hiddenFields);
        showUndoToast(`Hidden field ${label} removed`);
        this._render();
      });
    });

    // Hidden fields — edit name/value
    el.querySelectorAll('.pb-form-hidden-row').forEach((row) => {
      const idx = parseInt(row.dataset.hiddenIdx, 10);
      const nameInput = row.querySelector('.pb-form-hidden-name');
      const valueInput = row.querySelector('.pb-form-hidden-value');

      const commit = () => {
        const config = this._getFormConfig();
        if (config.hiddenFields[idx]) {
          config.hiddenFields[idx].name = nameInput.value.trim();
          config.hiddenFields[idx].value = valueInput.value.trim();
          this._updateConfig('hiddenFields', config.hiddenFields);
        }
      };
      nameInput?.addEventListener('change', commit);
      valueInput?.addEventListener('change', commit);
    });

    // Add hidden field
    el.querySelector('#pb-form-add-hidden')?.addEventListener('click', () => {
      const config = this._getFormConfig();
      config.hiddenFields.push({ name: '', value: '' });
      this._updateConfig('hiddenFields', config.hiddenFields);
      this._render();
    });
  }

  destroy() {
    this._destroyed = true;
    store.commitTransaction();
  }
}

function getActionPlaceholder(endpoint) {
  switch (endpoint) {
    case 'netlify':
      return 'Form name (e.g. contact)';
    case 'formspree':
      return 'Formspree form ID (e.g. xyzabc)';
    case 'worker':
      return 'Worker URL';
    default:
      return 'https://example.com/submit';
  }
}

function esc(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
